/**
 * Shared utility for grouping SentenceGroupType items by their category.
 *
 * Used by SentenceGroupSelect (single-select), StudentsView (multi-select),
 * and any other dropdown that displays sentence groups.
 */
import type { SentenceGroupCategoryType, SentenceGroupType } from 'types/types'

export type CategoryHeader = { _categoryDescription: string; _header: true }
export type GroupedSentenceGroupItem = CategoryHeader | SentenceGroupType

export function isCategoryHeader(item: GroupedSentenceGroupItem): item is CategoryHeader {
  return '_header' in item
}

/**
 * Groups sentence groups by category, inserting disabled header items between groups.
 * Headers are only inserted when there are 2+ distinct categories.
 * Categories are sorted by their `order` field; uncategorized items appear last.
 * Within each category, items retain their existing sort order.
 */
export function groupSentenceGroupsByCategory(items: ReadonlyArray<SentenceGroupType>): Array<GroupedSentenceGroupItem> {
  const byCategory = new Map<string, { category: SentenceGroupCategoryType | null; items: SentenceGroupType[] }>()
  for (const sg of items) {
    const key = sg.category?.id ?? '_none'
    if (!byCategory.has(key)) {
      byCategory.set(key, { category: sg.category ?? null, items: [] })
    }
    byCategory.get(key)!.items.push(sg)
  }

  const sortedCategories = [...byCategory.entries()].toSorted(([, a], [, b]) => {
    if (!a.category) {
      return 1
    }
    if (!b.category) {
      return -1
    }
    return a.category.order - b.category.order
  })

  const showHeaders = sortedCategories.length > 1
  const result: Array<GroupedSentenceGroupItem> = []
  for (const [, { category, items: groupItems }] of sortedCategories) {
    if (showHeaders) {
      result.push({ _categoryDescription: category?.description ?? 'Uncategorized', _header: true })
    }
    result.push(...groupItems)
  }
  return result
}

export function groupedItemTitle(item: GroupedSentenceGroupItem): string {
  if (isCategoryHeader(item)) {
    return item._categoryDescription
  }
  return item.reading.description ?? ''
}

export function groupedItemProps(item: GroupedSentenceGroupItem): Record<string, unknown> {
  if (isCategoryHeader(item)) {
    return { disabled: true }
  }
  return {}
}

export function groupedItemValue(item: GroupedSentenceGroupItem): string {
  if (isCategoryHeader(item)) {
    return `_header_${item._categoryDescription}`
  }
  return item.reading.id
}
